import * as React from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import clsx from 'clsx';
import CssBaseline from '@material-ui/core/CssBaseline';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import {
    ExitToAppOutlined, DataUsageOutlined, GetAppOutlined, CategoryOutlined, DashboardOutlined,
    SearchOutlined, AdjustOutlined, QueueMusicOutlined, SubscriptionsOutlined, CompareArrowsOutlined,
    CreditCardOutlined
} from '@material-ui/icons'
import { Link } from 'react-router-dom';
import { UserContextConsumer } from './components/context/user';
import { BPM_ANALYTIS, BPM_SOUNDS_LOGO_SVG, BPM_SUPEME_LOGO_WHITE_SVG, PLATFORM_GRAY_SVG } from './assets';
import { DateBox, Autocomplete, DropDownButton } from 'devextreme-react';
import { InitialFilterValues, FilterContextProvider, FilterParams } from './components/context/filter';
import * as moment from 'moment'
import { BPMAnalyticsAPI } from './api';
import CustomStore from 'devextreme/data/custom_store';
import { useHistory, useLocation } from "react-router-dom";
import { Collapse } from '@material-ui/core';
import * as qs from 'query-string'
import { Environment } from './env';
import { Library, Platform } from './components/context/platform';
import { PlatformRenderer, sp } from './utils/platformSelect';
import { APIVersion, AuthenticatedRequest, HTTPMethod } from 'api/src/requests/request';

const drawerWidth = 240;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            backgroundColor: theme.palette.primary.dark
        },
        appBar: {
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            })
        },
        appBarShift: {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: drawerWidth,
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        menuButton: {
            marginRight: theme.spacing(2),
        },
        hide: {
            display: 'none',
        },
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
        },
        drawerPaper: {
            width: drawerWidth,
            backgroundColor: '#171717'
        },
        nested: {
            paddingLeft: theme.spacing(4),
        },
        drawerHeader: {
            display: 'flex',
            alignItems: 'center',
            ...theme.mixins.toolbar,
            justifyContent: 'flex-end',
        },
        logo: {
            padding: theme.spacing(4, 4)
        },
        logoImg: {
            width: '90%',
            marginLeft: 'auto',
            marginRight: 'auto'
        },
        content: {
            flexGrow: 1,
            padding: theme.spacing(3),
            paddingTop: theme.spacing(1),
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: -drawerWidth,
            minHeight: '100vh',
        },
        contentShift: {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
            maxWidth: `calc(100% - ${drawerWidth}px)`
        },
    }),
);

const supremeSearchDataSource = new CustomStore({
    load: (opts) => {
        const apiRequest = new AuthenticatedRequest<{}>('/albums', HTTPMethod.GET, APIVersion.v1_0, { term: opts.searchValue });
        apiRequest.overrideAPIUrl = `https://api.download.bpmsupreme.com`

        return apiRequest.send().then((response) => {
            return response.data
        });
    }
})

const createSearchDataSource = new CustomStore({
    load: (opts) => {
        return BPMAnalyticsAPI.get('search', { term: opts.searchValue }, undefined, Environment.CREATE_API) as Promise<string>
    }
})

const libraries = [
    { name: 'Open Format', value: 'main' },
    { name: 'Latino', value: 'latin' },
    { name: 'Artist', value: 'artist' },
    { name: 'No Filter', value: undefined },
]

export default function Menu(props: { children: React.ReactNode }) {
    const classes = useStyles();
    const location = useLocation()

    const [startDate, setStartDate] = React.useState(InitialFilterValues.start_date)
    const [endDate, setEndDate] = React.useState(InitialFilterValues.end_date)
    const startOfLastMonth = React.useMemo(() => moment().startOf('month').toDate(), [])
    const [financialMode, _setFinancialMode] = React.useState(false)

    const setFinancialMode = React.useCallback((activate) => {
        _setFinancialMode(activate)
        if (activate) {
            if (moment(endDate).isAfter(startOfLastMonth)) {
                setEndDate(startOfLastMonth)
                setStartDate(moment(startOfLastMonth).add(-4, 'months').toDate())
            }
        }
    }, [endDate])


    const [searchTerm, setSearchTerm] = React.useState('')
    const [searchValue, setSearchValue] = React.useState('')
    const [filterParams, _setFilterParams] = React.useState({ ...InitialFilterValues, setFinancialMode })
    const [didShowDetailPage, setDidShowDetailPage] = React.useState(false)
    const [platformOpen, setPlatformOpen] = React.useState(false)
    const [currentPlatform, setCurrentPlatform] = React.useState<Platform>(InitialFilterValues.platform)
    const [currentLibrary, setCurrentLibrary] = React.useState<Library | undefined>(InitialFilterValues.library)
    const [statisticsOpen, setStatisticsOpen] = React.useState(location.pathname.startsWith('/statistics'))
    const [productOpen, setProductOpen] = React.useState(location.pathname.startsWith('/product'))
    const [showDateFilter, setShowDateFilter] = React.useState(true)
    const [showLibrarySelector, setShowLibrarySelector] = React.useState(false)

    const platformRef = React.useRef(currentPlatform);

    const endDateBox = React.useRef<DateBox>();

    const router = useHistory()

    React.useEffect(() => {
        platformRef.current = currentPlatform;
    }, [currentPlatform])

    const setFilterParams = (params: Omit<FilterParams, 'setFinancialMode'>) => {
        _setFilterParams({
            ...params,
            setFinancialMode: setFinancialMode
        })
        history.replaceState(undefined, document.title, '?' + qs.stringify(params))
    }

    React.useEffect(() => {
        const originalFetch = fetch;
        window.fetch = (url, opts) => {
            if (!url.toString().includes('platform=')) {
                if (!url.toString().includes('?')) {
                    url = url.toString() + '?platform=' + platformRef.current
                } else {
                    url = url.toString() + '&platform=' + platformRef.current
                }
            }
            return originalFetch(url, opts);
        };
    }, [])

    React.useEffect(() => {
        if (location.pathname.startsWith('/album/')) {
            setDidShowDetailPage(true)
            let album_id = location.pathname.split('/').pop()
            BPMAnalyticsAPI.get('search', { album_id }, undefined, sp(currentPlatform, { create: Environment.CREATE_API, latino: Environment.LATINO_API, supreme: Environment.API })).then((data: any) => {
                setSearchValue(sp(currentPlatform, { default: 'Album: ', create: 'Sound Pack: ' }) + data.suggestion)
            })
        } else if (didShowDetailPage) {
            setDidShowDetailPage(false)
            setSearchTerm('')
            setSearchValue('')
        }
    }, [location])

    const setPlatform = () => {
        document.documentElement.style.setProperty('--platform', 'var(--' + currentPlatform + ')');
        document.documentElement.style.setProperty('--platform-text-color', '' + currentPlatform == 'create' ? 'white' : 'white' + '');

        document.dispatchEvent(new CustomEvent('platform', { detail: currentPlatform }))
    }

    React.useEffect(() => {
        setPlatform()
        setShowLibrarySelector(currentPlatform === 'supreme');
    }, [currentPlatform])

    React.useEffect(() => {
        document.addEventListener('datefilter', (val: CustomEvent) => {
            setShowDateFilter(val.detail as boolean)
        })
    }, [])

    return (
        <div className={classes.root}>
            <CssBaseline />
            <Drawer
                className={classes.drawer}
                variant="persistent"
                anchor="left"
                open
                classes={{
                    paper: classes.drawerPaper,
                }}>
                <UserContextConsumer>{() => {
                    return <>
                        <div className={clsx(classes.drawerHeader, classes.logo)}>
                            <img className={classes.logoImg} src={currentPlatform == 'supreme' ? BPM_SUPEME_LOGO_WHITE_SVG : BPM_SOUNDS_LOGO_SVG} />
                        </div>
                        <Divider />
                        <List className='menu'>
                            <Link to='/dashboard' style={{ textDecoration: 'none' }} className={location.pathname == '/dashboard' ? 'active' : ''}>
                                <ListItem button>
                                    <ListItemIcon>
                                        <DashboardOutlined />
                                    </ListItemIcon>
                                    <ListItemText primary={'Dashboard'} />
                                </ListItem>
                            </Link>
                            <ListItem button onClick={() => setStatisticsOpen(!statisticsOpen)}>
                                <ListItemIcon>
                                    <DataUsageOutlined />
                                </ListItemIcon>
                                <ListItemText color={'white'} primary={'Statistics'} />
                            </ListItem>
                            <Collapse in={statisticsOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <Link to='/statistics/downloads' style={{ textDecoration: 'none' }} className={location.pathname == '/statistics/downloads' ? 'active' : ''}>
                                        <ListItem button className={classes.nested}>
                                            <ListItemIcon>
                                                <GetAppOutlined />
                                            </ListItemIcon>
                                            <ListItemText primary={'Downloads'} />
                                        </ListItem>
                                    </Link>
                                    <Link to='/statistics/category' style={{ textDecoration: 'none' }} className={location.pathname == '/statistics/category' ? 'active' : ''}>
                                        <ListItem button className={classes.nested}>
                                            <ListItemIcon>
                                                <CategoryOutlined />
                                            </ListItemIcon>
                                            <ListItemText primary={sp(currentPlatform, { default: 'Category', create: 'Genre' })} />
                                        </ListItem>
                                    </Link>
                                    <Link to='/statistics/search' style={{ textDecoration: 'none' }} className={location.pathname == '/statistics/search' ? 'active' : ''}>
                                        <ListItem button className={classes.nested}>
                                            <ListItemIcon>
                                                <SearchOutlined />
                                            </ListItemIcon>
                                            <ListItemText primary={'Search'} />
                                        </ListItem>
                                    </Link>
                                    <Link to='/statistics/browse' style={{ textDecoration: 'none' }} className={location.pathname == '/statistics/browse' ? 'active' : ''}>
                                        <ListItem button className={classes.nested}>
                                            <ListItemIcon>
                                                <AdjustOutlined />
                                            </ListItemIcon>
                                            <ListItemText primary={'Browse'} />
                                        </ListItem>
                                    </Link>
                                    <Link to='/statistics/playlist' style={{ textDecoration: 'none' }} className={(location.pathname == '/statistics/playlist' ? 'active' : '') + (' hide-latino hide-create')}>
                                        <ListItem button className={classes.nested}>
                                            <ListItemIcon>
                                                <QueueMusicOutlined />
                                            </ListItemIcon>
                                            <ListItemText primary={'Playlist'} />
                                        </ListItem>
                                    </Link>
                                    <Link to='/statistics/curated' style={{ textDecoration: 'none' }} className={location.pathname == '/statistics/curated' ? 'active' : ''}>
                                        <ListItem button className={classes.nested}>
                                            <ListItemIcon>
                                                <SubscriptionsOutlined />
                                            </ListItemIcon>
                                            <ListItemText primary={'Curated'} />
                                        </ListItem>
                                    </Link>
                                    <Link to='/statistics/compare' style={{ textDecoration: 'none' }} className={location.pathname == '/statistics/compare' ? 'active' : ''}>
                                        <ListItem button className={classes.nested}>
                                            <ListItemIcon>
                                                <CompareArrowsOutlined />
                                            </ListItemIcon>
                                            <ListItemText primary={'Compare'} />
                                        </ListItem>
                                    </Link>
                                </List>
                            </Collapse>
                            <ListItem button onClick={() => setProductOpen(!productOpen)}>
                                <ListItemIcon>
                                    <DataUsageOutlined />
                                </ListItemIcon>
                                <ListItemText color={'white'} primary={'Product'} />
                            </ListItem>
                            <Collapse in={productOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <Link to='/product/pages' style={{ textDecoration: 'none' }} className={location.pathname == '/product/pages' ? 'active' : ''}>
                                        <ListItem button className={classes.nested}>
                                            <ListItemIcon>
                                                <GetAppOutlined />
                                            </ListItemIcon>
                                            <ListItemText primary={'Pages'} />
                                        </ListItem>
                                    </Link>
                                    <Link to='/product/clicks' style={{ textDecoration: 'none' }} className={location.pathname == '/product/clicks' ? 'active' : ''}>
                                        <ListItem button className={classes.nested}>
                                            <ListItemIcon>
                                                <GetAppOutlined />
                                            </ListItemIcon>
                                            <ListItemText primary={'Clicks'} />
                                        </ListItem>
                                    </Link>
                                </List>
                            </Collapse>
                            <Link to='/financial' style={{ textDecoration: 'none' }} className={location.pathname == '/financial' ? 'active' : ''}>
                                <ListItem button>
                                    <ListItemIcon>
                                        <DashboardOutlined />
                                    </ListItemIcon>
                                    <ListItemText primary={'Financial'} />
                                </ListItem>
                            </Link>
                            <PlatformRenderer only="create">
                                <Link to='/credits' style={{ textDecoration: 'none' }} className={location.pathname == '/credits' ? 'active' : ''}>
                                    <ListItem button>
                                        <ListItemIcon>
                                            <CreditCardOutlined />
                                        </ListItemIcon>
                                        <ListItemText primary={'Credits'} />
                                    </ListItem>
                                </Link>
                            </PlatformRenderer>
                            <Link to='/logout' style={{ textDecoration: 'none' }}>
                                <ListItem button>
                                    <ListItemIcon>
                                        <ExitToAppOutlined />
                                    </ListItemIcon>
                                    <ListItemText primary={'Logout'} />
                                </ListItem>
                            </Link>
                        </List>
                        <div className={clsx(classes.drawerHeader, classes.logo)} style={{ marginTop: 'auto' }}>
                            <img className={classes.logoImg} src={BPM_ANALYTIS} />
                        </div>
                    </>
                }}</UserContextConsumer>
            </Drawer>
            <main className={clsx(classes.content, {
                [classes.contentShift]: open,
            })}>
                <div className={classes.drawerHeader}>
                    <div className='search-container' >
                        <SearchOutlined className='placeholder-img active' />
                        <Autocomplete dataSource={{ store: sp(currentPlatform, { supreme: supremeSearchDataSource, latino: supremeSearchDataSource, create: createSearchDataSource }), paginate: false }} className='header-comp search-autocomplete'
                            valueExpr={'id'} displayExpr={'name'} wrapItemText openOnFieldClick
                            itemRender={(item: { artist: string, name: string }) => {
                                return <div className='search-item'>
                                    <span className='search-result'>
                                        {item.artist} - {item.name}
                                    </span>
                                </div>
                            }} onSelectionChanged={(val) => {
                                if (val.selectedItem && typeof val.selectedItem === 'object') {
                                    setSearchTerm(val.selectedItem.artist + ' - ' + val.selectedItem.name)
                                    setSearchValue(val.selectedItem)
                                    router.push('/album/' + val.selectedItem.id)
                                } else {
                                    setSearchTerm(val.selectedItem)
                                    setSearchValue(val.selectedItem)
                                }
                            }} value={searchValue} onValueChange={(e) => setSearchValue(e)} placeholder={'Search'} searchTimeout={100} />
                    </div>
                    {showLibrarySelector && <div style={{ paddingRight: 8, paddingLeft: 8, paddingTop: 4, paddingBottom: 4 }}>
                        <DropDownButton text={libraries.find(l => l.value == currentLibrary)?.name || 'Library'} className='rel-lib-selector' stylingMode='text' useSelectMode={false}
                            displayExpr="name"
                            keyExpr="name" items={libraries} onItemClick={(e) => {
                                setCurrentLibrary(e.itemData?.value)
                                setFilterParams({ ...filterParams, library: e.itemData?.value })
                            }} />
                    </div>}
                    {!showDateFilter && <div style={{ paddingRight: 8, paddingLeft: 8, paddingTop: 4, paddingBottom: 4 }}>
                        <div className="title" style={{ color: 'var(--inactive)' }}>No Date Filter Available</div>
                    </div>}
                    {showDateFilter && <>
                        <div style={{ paddingRight: 8, paddingLeft: 8, paddingTop: 4, paddingBottom: 4 }}>
                            <DropDownButton text='Rel' className='rel-date-selector' stylingMode='text' useSelectMode={false}
                                displayExpr="name"
                                keyExpr="name" items={[
                                    { name: 'This Week' },
                                    { name: 'Last 7 Days' },
                                    { name: 'Last Week' },
                                    { name: 'This Month' },
                                    { name: 'Last Month' },
                                    { name: 'This Year' },
                                    { name: 'Last Year' },
                                    { name: 'All Time' }
                                ]} onItemClick={(e) => {
                                    switch (e.itemData?.name) {
                                        case 'This Week': {
                                            const start = moment().startOf('week').toDate()
                                            const end = moment().endOf('week').toDate()
                                            setStartDate(start)
                                            setEndDate(end)
                                            setFilterParams({ start_date: start, end_date: end, platform: currentPlatform })
                                        }
                                            break;
                                        case 'Last 7 Days': {
                                            const start = moment().add(-7, 'days').startOf('day').toDate()
                                            const end = moment().endOf('day').toDate()
                                            setStartDate(start)
                                            setEndDate(end)
                                            setFilterParams({ start_date: start, end_date: end, platform: currentPlatform })
                                        }
                                            break;
                                        case 'Last Week': {
                                            const start = moment().add(-1, 'week').startOf('week').toDate()
                                            const end = moment().add(-1, 'week').endOf('week').toDate()
                                            setStartDate(start)
                                            setEndDate(end)
                                            setFilterParams({ start_date: start, end_date: end, platform: currentPlatform })
                                        }
                                            break;
                                        case 'Last Month': {
                                            const start = moment().add(-1, 'month').startOf('month').toDate()
                                            const end = moment().add(-1, 'month').endOf('month').toDate()
                                            setStartDate(start)
                                            setEndDate(end)
                                            setFilterParams({ start_date: start, end_date: end, platform: currentPlatform })
                                        }
                                            break;
                                        case 'This Month': {
                                            const start = moment().startOf('month').toDate()
                                            const end = moment().endOf('month').toDate()
                                            setStartDate(start)
                                            setEndDate(end)
                                            setFilterParams({ start_date: start, end_date: end, platform: currentPlatform })
                                        }
                                            break;
                                        case 'Last Week': {
                                            const start = moment().add(-1, 'week').startOf('week').toDate()
                                            const end = moment().add(-1, 'week').endOf('week').toDate()
                                            setStartDate(start)
                                            setEndDate(end)
                                            setFilterParams({ start_date: start, end_date: end, platform: currentPlatform })
                                        }
                                            break;
                                        case 'This Year': {
                                            const start = moment().startOf('year').toDate()
                                            const end = moment().endOf('year').toDate()
                                            setStartDate(start)
                                            setEndDate(end)
                                            setFilterParams({ start_date: start, end_date: end, platform: currentPlatform })
                                        }
                                            break;
                                        case 'Last Year': {
                                            const start = moment().add(-1, 'year').startOf('year').toDate()
                                            const end = moment().add(-1, 'year').endOf('year').toDate()
                                            setStartDate(start)
                                            setEndDate(end)
                                            setFilterParams({ start_date: start, end_date: end, platform: currentPlatform })
                                        }
                                            break;
                                        case 'All Time': {
                                            const start = moment(981000000000).toDate()
                                            const end = moment().toDate()
                                            setStartDate(start)
                                            setEndDate(end)
                                            setFilterParams({ start_date: start, end_date: end, platform: currentPlatform })
                                        }
                                            break;
                                    }
                                }} />
                        </div>
                        <div style={{ paddingRight: 8, paddingLeft: 8, paddingTop: 4, paddingBottom: 4 }}>
                            <DateBox className='header-comp dropdown-inline' displayFormat='d MMMM yyyy' max={endDate} value={startDate} onValueChanged={(e) => {
                                const startDateStartOfDay = moment(e.value).startOf('day').toDate()
                                setStartDate(startDateStartOfDay)
                                e.component!.instance().close()
                                if (endDateBox.current && filterParams.start_date.getTime() != startDateStartOfDay.getTime()) {
                                    endDateBox.current.instance.open()
                                }
                            }} openOnFieldClick disabledDates={(date) => {
                                if (financialMode && date.date.getDate() !== 1) {
                                    return true
                                }
                                return false
                            }} onClosed={() => {
                                if (endDateBox.current && filterParams.start_date.getTime() != startDate.getTime())
                                    endDateBox.current.instance.open()
                            }} />
                        </div>
                        <div style={{ paddingRight: 8, paddingLeft: 8, paddingTop: 4, paddingBottom: 4, marginLeft: 16 }}>
                            <DateBox ref={(ref) => endDateBox.current = ref!} className='header-comp dropdown-inline' displayFormat='d MMMM yyyy' min={startDate} max={financialMode ? startOfLastMonth : undefined} value={endDate} onValueChanged={(e) => {
                                const endDateEndOfDay = moment(e.value).endOf('day').toDate()
                                setEndDate(endDateEndOfDay)
                                setFilterParams({ start_date: startDate, end_date: endDateEndOfDay, platform: currentPlatform })
                            }} openOnFieldClick disabledDates={(date) => {
                                if (financialMode && date.date.getDate() !== 1) {
                                    return true
                                }
                                return false
                            }} onClosed={() => {
                                if (endDate.getTime() == filterParams.end_date.getTime() && startDate.getTime() != filterParams.start_date.getTime()) {
                                    // did not change end date but start date
                                    setFilterParams({ start_date: startDate, end_date: endDate, platform: currentPlatform })
                                }
                            }} />
                        </div>
                    </>}
                    <div className={`platform hide-mobile`}
                        style={{ position: 'relative', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}
                        onClick={() => {
                            setPlatformOpen(!platformOpen)
                        }}>
                        <img src={PLATFORM_GRAY_SVG} />
                    </div>
                    <div className={'platform-selection ' + (platformOpen ? 'slide-down fadeToggle' : 'slide-up')}>
                        <div className="switch-text">Switch Analytics to:</div>
                        <div className="divider"></div>
                        <div className='platform-selection-item' onClick={() => {
                            setCurrentPlatform('create')
                            setPlatformOpen(false)
                            setFilterParams({ start_date: startDate, end_date: endDate, platform: 'create' })
                        }}>
                            <div className='img'>
                                <img src={BPM_SOUNDS_LOGO_SVG} />
                            </div>
                        </div>
                        <div className='platform-selection-item' onClick={() => {
                            setCurrentPlatform('supreme')
                            setPlatformOpen(false)
                            setFilterParams({ start_date: startDate, end_date: endDate, platform: 'supreme' })
                        }}>
                            <div className='img'>
                                <img src={BPM_SUPEME_LOGO_WHITE_SVG} />
                            </div>
                        </div>
                        <div className="switch-text">Switch to Admin Panel:</div>
                        <div className="divider"></div>
                        <div className='platform-selection-item' onClick={() => {
                            window.open('https://admin.create.bpmsupreme.com', '__blank')
                        }}>
                            <div className='img'>
                                <img src={BPM_SOUNDS_LOGO_SVG} />
                            </div>
                        </div>
                        <div className='platform-selection-item' onClick={() => {
                            window.open('https://admin.bpmsupreme.com', '__blank')
                        }}>
                            <div className='img'>
                                <img src={BPM_SUPEME_LOGO_WHITE_SVG} />
                            </div>
                        </div>
                    </div>

                </div>
                <FilterContextProvider value={filterParams}>
                    {props.children}
                </FilterContextProvider>
            </main>
        </div >
    );
}




// WEBPACK FOOTER //
// ./src/Menu.tsx